import React from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import { Chart } from 'react-google-charts';
import ru from 'date-fns/locale/ru';
import styles from './ReferralAnalytics.module.css';
import 'react-datepicker/dist/react-datepicker.css';

// Register Russian locale for date picker
registerLocale('ru', ru);

// Tooltip component for metric explanations
function Tooltip({ text }) {
    return (
        <span className={styles.tooltipWrapper}>
            <span className={styles.tooltipIcon}>?</span>
            <span className={styles.tooltipText}>{text}</span>
        </span>
    );
}

export default function ReferralAnalytics({
    analytics,
    isLoading,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    codesItems = [],
    codesPagination = { has_more: false, next_cursor: '', prev_cursor: '' },
    codesLoading = false,
    phoneInput = '',
    onPhoneChange = () => {},
    onNextPage = () => {},
    onPrevPage = () => {},
}) {
    const { t } = useTranslation();

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>{t('loading') || 'Loading...'}</div>
            </div>
        );
    }

    if (!analytics) {
        return (
            <div className={styles.container}>
                <div className={styles.noData}>{t('NSReferralAnalytics.noData')}</div>
            </div>
        );
    }

    // Prepare funnel data for chart
    const funnelData = [
        ['Status', 'Count', { role: 'style' }],
        [t('NSReferralAnalytics.invited'), analytics.status_breakdown?.invited || 0, '#4CAF50'],
        [t('NSReferralAnalytics.accepted'), analytics.status_breakdown?.accepted || 0, '#8BC34A'],
        [t('NSReferralAnalytics.registered'), analytics.status_breakdown?.registered || 0, '#CDDC39'],
        [t('NSReferralAnalytics.completed'), analytics.status_breakdown?.completed || 0, '#FFC107'],
    ];

    // Prepare trends data for line chart
    const trendsChartData = analytics.trends_data?.length > 0
        ? [
            [t('NSReferralAnalytics.date') || 'Date', t('NSReferralAnalytics.invited'), t('NSReferralAnalytics.registrations')],
            ...analytics.trends_data.map(item => [
                item.date,
                item.invites,
                item.registrations,
            ])
        ]
        : null;

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>{t('NSReferralAnalytics.title')}</h1>

            {/* Date Range Picker */}
            <div className={styles.datePickerRow}>
                <div className={styles.datePickerGroup}>
                    <label>{t('NSReferralAnalytics.selectPeriod')}</label>
                    <div className={styles.datePickers}>
                        <DatePicker
                            selected={startDate}
                            onChange={date => setStartDate(date)}
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            placeholderText={t('from') || 'From'}
                            className={styles.datePicker}
                            dateFormat="dd.MM.yyyy"
                            locale="ru"
                        />
                        <span className={styles.dateSeparator}>-</span>
                        <DatePicker
                            selected={endDate}
                            onChange={date => setEndDate(date)}
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate}
                            placeholderText={t('till') || 'To'}
                            className={styles.datePicker}
                            dateFormat="dd.MM.yyyy"
                            locale="ru"
                        />
                    </div>
                </div>
                {(startDate || endDate) && (
                    <button
                        className={styles.clearButton}
                        onClick={() => {
                            setStartDate(null);
                            setEndDate(null);
                        }}
                    >
                        {t('NSReferralAnalytics.allTime')}
                    </button>
                )}
            </div>

            {/* Summary Cards */}
            <div className={styles.cardsRow}>
                <div className={styles.card}>
                    <div className={styles.cardValue}>{analytics.total_invites_sent || 0}</div>
                    <div className={styles.cardLabel}>{t('NSReferralAnalytics.totalInvites')}</div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardValue}>{analytics.total_registrations || 0}</div>
                    <div className={styles.cardLabel}>{t('NSReferralAnalytics.registrations')}</div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardValue}>{analytics.total_successful || 0}</div>
                    <div className={styles.cardLabel}>
                        {t('NSReferralAnalytics.successful')}
                        <Tooltip text={t('NSReferralAnalytics.successfulTooltip')} />
                    </div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardValue}>{(analytics.total_bonuses_awarded || 0).toLocaleString()}</div>
                    <div className={styles.cardLabel}>{t('NSReferralAnalytics.bonusesAwarded')}</div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardValue}>{Number(analytics.conversion_rate || 0).toFixed(1)}%</div>
                    <div className={styles.cardLabel}>
                        {t('NSReferralAnalytics.conversionRate')}
                        <Tooltip text={t('NSReferralAnalytics.conversionRateTooltip')} />
                    </div>
                </div>
            </div>

            {/* Bonus Breakdown */}
            <div className={styles.bonusRow}>
                <div className={styles.bonusCard}>
                    <div className={styles.bonusValue}>{(analytics.total_referrer_bonuses || 0).toLocaleString()}</div>
                    <div className={styles.bonusLabel}>
                        {t('NSReferralAnalytics.referrerBonuses')}
                        <Tooltip text={t('NSReferralAnalytics.referrerBonusesTooltip')} />
                    </div>
                </div>
                <div className={styles.bonusCard}>
                    <div className={styles.bonusValue}>{(analytics.total_referee_bonuses || 0).toLocaleString()}</div>
                    <div className={styles.bonusLabel}>
                        {t('NSReferralAnalytics.refereeBonuses')}
                        <Tooltip text={t('NSReferralAnalytics.refereeBonusesTooltip')} />
                    </div>
                </div>
            </div>

            {/* Charts Row */}
            <div className={styles.chartsRow}>
                {/* Funnel Chart */}
                <div className={styles.chartCard}>
                    <h3 className={styles.chartTitle}>{t('NSReferralAnalytics.funnel')}</h3>
                    <Chart
                        chartType="BarChart"
                        width="100%"
                        height="300px"
                        data={funnelData}
                        options={{
                            legend: { position: 'none' },
                            hAxis: { minValue: 0, textStyle: { color: '#666' } },
                            vAxis: { textStyle: { color: '#666' } },
                            chartArea: { width: '70%', height: '80%', backgroundColor: '#fff' },
                            backgroundColor: '#fff',
                            colors: ['#73D389', '#8BC34A', '#CDDC39', '#FFC107'],
                        }}
                    />
                </div>

                {/* Trends Chart */}
                {trendsChartData && (
                    <div className={styles.chartCard}>
                        <h3 className={styles.chartTitle}>{t('NSReferralAnalytics.trends')}</h3>
                        <Chart
                            chartType="LineChart"
                            width="100%"
                            height="300px"
                            data={trendsChartData}
                            options={{
                                curveType: 'function',
                                legend: { position: 'bottom', textStyle: { color: '#666' } },
                                hAxis: { textStyle: { color: '#666' } },
                                vAxis: { textStyle: { color: '#666' } },
                                chartArea: { width: '85%', height: '70%', backgroundColor: '#fff' },
                                backgroundColor: '#fff',
                                colors: ['#73D389', '#705AFB'],
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Top Referrers Table */}
            {analytics.top_referrers?.length > 0 && (
                <div className={styles.tableCard}>
                    <h3 className={styles.tableTitle}>{t('NSReferralAnalytics.topReferrers')}</h3>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{t('NSReferralAnalytics.phone')}</th>
                                <th>{t('NSReferralAnalytics.referralCount')}</th>
                                <th>{t('NSReferralAnalytics.totalBonus')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {analytics.top_referrers.map((referrer, index) => (
                                <tr key={referrer.user_id || index}>
                                    <td>{index + 1}</td>
                                    <td>{referrer.phone}</td>
                                    <td>{referrer.referral_count}</td>
                                    <td>{(referrer.total_bonus || 0).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Referral Codes Table - Top 10 */}
            {analytics.top_referral_codes?.length > 0 && (
                <div className={styles.tableCard}>
                    <h3 className={styles.tableTitle}>{t('NSReferralAnalytics.topReferralCodes')}</h3>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{t('NSReferralAnalytics.codeOwner')}</th>
                                <th>{t('NSReferralAnalytics.code')}</th>
                                <th className={styles.headerWithTooltip}>
                                    {t('NSReferralAnalytics.usageCount')}
                                    <Tooltip text={t('NSReferralAnalytics.usageCountTooltip')} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {analytics.top_referral_codes.map((code, index) => (
                                <tr key={code.code || index}>
                                    <td>{index + 1}</td>
                                    <td>{code.owner_phone || '-'}</td>
                                    <td className={styles.codeCell}>{code.code}</td>
                                    <td>{code.usage_count}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className={styles.tableCard}>
                <h3 className={styles.tableTitle}>{t('NSReferralAnalytics.allReferralCodes')}</h3>
                <div style={{ marginBottom: 12 }}>
                    <input
                        type="text"
                        value={phoneInput}
                        onChange={e => onPhoneChange(e.target.value)}
                        placeholder={t('NSReferralAnalytics.searchByPhone')}
                        className={styles.datePicker}
                        style={{ width: 280 }}
                    />
                </div>
                {codesLoading ? (
                    <div className={styles.loading}>{t('loading') || 'Loading...'}</div>
                ) : codesItems.length === 0 ? (
                    <div className={styles.noData}>{t('NSReferralAnalytics.noReferralCodesFound')}</div>
                ) : (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{t('NSReferralAnalytics.codeOwner')}</th>
                                <th>{t('NSReferralAnalytics.code')}</th>
                                <th className={styles.headerWithTooltip}>
                                    {t('NSReferralAnalytics.usageCount')}
                                    <Tooltip text={t('NSReferralAnalytics.usageCountTooltip')} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {codesItems.map((code, index) => (
                                <tr key={code.code || index}>
                                    <td>{index + 1}</td>
                                    <td>{code.owner_phone || '-'}</td>
                                    <td className={styles.codeCell}>{code.code}</td>
                                    <td>{code.usage_count}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 16, justifyContent: 'flex-end' }}>
                    <button
                        type="button"
                        className={styles.clearButton}
                        onClick={onPrevPage}
                        disabled={!codesPagination?.prev_cursor}
                        style={{ opacity: codesPagination?.prev_cursor ? 1 : 0.5, cursor: codesPagination?.prev_cursor ? 'pointer' : 'not-allowed' }}
                    >
                        {t('NSReferralAnalytics.prevPage')}
                    </button>
                    <button
                        type="button"
                        className={styles.clearButton}
                        onClick={onNextPage}
                        disabled={!codesPagination?.has_more}
                        style={{ opacity: codesPagination?.has_more ? 1 : 0.5, cursor: codesPagination?.has_more ? 'pointer' : 'not-allowed' }}
                    >
                        {t('NSReferralAnalytics.nextPage')}
                    </button>
                </div>
            </div>
        </div>
    );
}
